import React from "react";
import { useSelector } from "react-redux";
import CurrencyConverter from "../../Currency/CurrencyConverter";
import {
  Award,
  People,
  Gem,
  BarChart,
  Percent,
  CurrencyDollar,
  Gift,
} from "react-bootstrap-icons";

const PoolBonusDetails = ({ tiles }) => {
  const currency = useSelector((state) => state.user?.selectedCurrency);
  const conversionFactor = useSelector((state) => state.user?.conversionFactor);

  const isLoading = !tiles;

  const {
    xNfts = 0,
    nftPrice = 0,
    personalBonus = 0,
    rank = "No Rank Achieved",
    teamVolume = 0,
    rankAdvanceBonus = 0,
    etsGrossProfit = 0,
    teamBonus = 0,
  } = tiles || {};

  const totalBonus = parseFloat(teamBonus) + parseFloat(personalBonus);

  const iconData = [
    {
      icon: <Gem size={22} color="gray" />,
      label: "MY X NFTS",
      value: `${xNfts} NFT`,
      sub: `${xNfts} * ${nftPrice} = ${xNfts * nftPrice} USDC`,
    },
    { icon: <Award size={22} color="gray" />, label: "MY RANK", value: rank },
    {
      icon: <Gift size={22} color="gray" />,
      label: "PERSONAL BONUS",
      value: personalBonus,
    },
    {
      icon: <Percent size={22} color="gray" />,
      label: "ETSX GROSS RESULT",
      value: `${etsGrossProfit}%`,
    },
    {
      icon: <BarChart size={22} color="gray" />,
      label: "TEAM BONUS",
      value: teamBonus,
    },
    {
      icon: <Award size={22} color="gray" />,
      label: "RANK ADVANCEMENT BONUS",
      value: rankAdvanceBonus,
    },
    {
      icon: <People size={22} color="gray" />,
      label: "TOTAL TEAM VOLUME",
      value: teamVolume,
      sub: "10 LEVELS DEEP",
    },
    {
      label: "TOTAL BONUS",
      value: totalBonus,
    },
  ];

  // === Styles ===
  const styles = {
    container: {
      background: "#fff",
      borderRadius: "6px",
      marginBottom: "1rem",
      padding: "24px",
      boxShadow: "0 6px 20px rgba(0,0,0,0.06)",
      margin: "auto",
    },
    header: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      fontSize: "20px",
      fontWeight: "600",
      marginBottom: "24px",
      color: "#333",
    },
    tileGrid: {
      display: "flex",
      flexWrap: "wrap",
      gap: "16px",
    },
    poolsWrapper: {
      display: "flex",
      flexWrap: "wrap",
      gap: "1rem",
      justifyContent: "center",
    },
    tile: {
      flex: "1 0 calc(33.333% - 12px)",
      background: "#fff",
      border: "1px solid #eee",
      borderRadius: "5px",
      padding: "25px 16px",
      display: "flex",
      flexDirection: "column",
      gap: "6px",
      boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
      minWidth: "200px",
      transition: "transform 0.2s",
    },
    label: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      fontSize: "13px",
      fontWeight: 600,
      color: "#555",
    },
    value: {
      fontSize: "14px",
      fontWeight: "500",
      color: "#222",
    },
    subText: {
      fontSize: "12px",
      color: "black",
      fontStyle: "italic",
    },
    skeletonBox: {
      height: "16px",
      backgroundColor: "#e0e0e0",
      borderRadius: "4px",
      marginBottom: "6px",
      width: "100%",
      animation: "pulse 1.5s infinite ease-in-out",
    },
  };

  // === Skeleton Loader ===
  const renderSkeleton = (key) => (
    <div style={styles.tile} key={key}>
      <div style={styles.label}>
        <div style={{ ...styles.skeletonBox, width: "60%" }} />
      </div>
      <div style={{ ...styles.skeletonBox, width: "40%" }} />
      <div style={{ ...styles.skeletonBox, width: "50%" }} />
    </div>
  );

  // === Format Currency Values ===
  const formatValue = (label, value) => {
    if (["ETSX GROSS RESULT", "MY RANK", "MY X NFTS"].includes(label)) return value;

    const symbol = currency?.symbolLeft ?? "$";
    return (
      <strong>
        {symbol} {CurrencyConverter(value ?? 0, conversionFactor)}
      </strong>
    );
  };

  return (
    <div className={styles.poolsWrapper}>
      <div style={styles.container}>
        <div style={styles.header}>Bonus Details</div>
        <div style={styles.tileGrid}>
          {isLoading
            ? Array.from({ length: 8 }).map((_, i) => renderSkeleton(i))
            : iconData.map((item, i) => (
                <div
                  key={i}
                  style={{
                    ...styles.tile,
                    ...(item.label === "TOTAL BONUS" && {
                      background: "#fafafa",
                    }),
                  }}
                >
                  <div style={styles.label}>
                    {item.icon} {item.label}
                  </div>
                  <div style={styles.value}>{formatValue(item.label, item.value)}</div>
                  {item.sub && <div style={styles.subText}>{item.sub}</div>}
                </div>
              ))}
        </div>
      </div>
    </div>
  );
};

export default PoolBonusDetails;
